import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Lock, Building2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Fetch available companies
        const fetchCompanies = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/companies/public`);
                setCompanies(response.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };
        fetchCompanies();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const user = await login(email, password, companyCode);
            toast.success('Sessão iniciada com sucesso!');
            
            // Redirect based on role
            if (user.role === 'master') {
                navigate('/companies');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            const message = err.response?.data?.detail || 'Erro ao iniciar sessão';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div
                className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1726866672851-5b99c837603c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMGluZHVzdHJpYWwlMjByb29mfGVufDB8fHx8MTc2ODE1NTE1OHww&ixlib=rb-4.1.0&q=85')`
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-emerald-900/70" />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_powermonitor-5/artifacts/rtabpwt5_Sem%20Fundo.png" 
                            alt="MedEletrões Logo" 
                            className="w-24 h-24 object-contain"
                        />
                        <div>
                            <h1 className="text-2xl font-bold font-heading">MedEletrões</h1>
                            <p className="text-sm text-emerald-200">Gestão de Energia</p>
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        <h2 className="text-4xl font-bold font-heading leading-tight">
                            Monitorize e otimize<br />
                            o seu consumo de energia
                        </h2>
                        <p className="text-lg text-slate-300 max-w-md">
                            Integre os seus dispositivos Shelly EM e 3EM para uma visão completa do consumo e produção energética.
                        </p>
                        <div className="flex gap-8">
                            <div>
                                <p className="text-3xl font-bold text-emerald-400">30%</p>
                                <p className="text-sm text-slate-400">Redução média de custos</p>
                            </div>
                            <div>
                                <p className="text-3xl font-bold text-emerald-400">24/7</p>
                                <p className="text-sm text-slate-400">Monitorização contínua</p>
                            </div>
                        </div>
                    </div>
                    
                    <p className="text-sm text-slate-400">
                        © 2024 MedEletrões. Desenvolvido por <a href="https://www.inforexe.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">Infor.Exe</a>
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_powermonitor-5/artifacts/rtabpwt5_Sem%20Fundo.png" 
                            alt="MedEletrões Logo" 
                            className="w-24 h-24 object-contain"
                        />
                        <div>
                            <h1 className="text-2xl font-bold font-heading text-slate-900">MedEletrões</h1>
                            <p className="text-sm text-slate-500">Gestão de Energia</p>
                        </div>
                    </div>

                    <Card className="border-0 shadow-xl">
                        <CardHeader className="space-y-1 pb-6">
                            <CardTitle className="text-2xl font-bold font-heading">Iniciar Sessão</CardTitle>
                            <CardDescription>
                                Introduza as suas credenciais para aceder à plataforma
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        <p>{error}</p>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="company">Empresa / Instância</Label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                                        <Select
                                            value={companyCode}
                                            onValueChange={setCompanyCode}
                                        >
                                            <SelectTrigger className="pl-10" data-testid="login-company">
                                                <SelectValue placeholder="Selecione a empresa" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="master">
                                                    <span className="flex items-center gap-2">
                                                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                                        Master Admin
                                                    </span>
                                                </SelectItem>
                                                {companies.map((company) => (
                                                    <SelectItem key={company.id} value={company.code}>
                                                        {company.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Contacte o seu administrador para obter o código da empresa
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="login-email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="login-password"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                                    disabled={loading || !companyCode}
                                    data-testid="login-submit"
                                >
                                    {loading ? 'A entrar...' : 'Entrar'}
                                </Button>
                            </form>

                            <div className="mt-6 text-center">
                                <p className="text-sm text-slate-500">
                                    Não tem conta?{' '}
                                    <Link
                                        to="/register"
                                        className="font-medium text-emerald-600 hover:text-emerald-700"
                                        data-testid="register-link"
                                    >
                                        Criar conta
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
